import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import type { ServiceRequestOptions } from '@dealstack/frontend/shared/other/react/ApiWrapper/types';

/** The bulk-sign REST actions a flow's endpoint builder must resolve. */
export type BulkSignEndpointAction = 'initiate' | 'signed' | 'prepare' | 'complete' | 'status';

/** FTN flow shape: synchronous prepare→sign→complete, or async initiate→poll→signed→complete. */
export type BulkSignMode = 'direct' | 'async';

/**
 * Per-call request-body fields describing which documents this signer targets and
 * how their signature was captured. Admin sends its greedy-or-explicit selection plus the
 * capture source; the public programme-token flow sends either `{}` (sign everything — the
 * backend derives the set from the token) or just `selectedDocumentWorkflowInstanceIds`.
 */
export type SignRequestFields = Record<string, unknown>;

/**
 * Flow-level wiring shared by every method sub-hook in a flow. Abstracts the admin
 * `${basePath}/eid-easy/…` routes and the public `/public-signing/programme/…?token=`
 * routes behind one endpoint builder, plus the request options each flow needs
 * (public: `{ skipLoginRedirect: true }`; admin: none).
 */
export interface BulkSignTransport {
	buildEndpoint: (action: BulkSignEndpointAction, jobId?: string) => string;
	requestOptions?: ServiceRequestOptions;
}

/** Params accepted by `makeRequest`, shared so callers can type their own wrappers. */
export type BulkSignRequestParams = Parameters<typeof makeRequest>[0];

/**
 * Issues a `makeRequest` call for a bulk-sign flow, forwarding the transport's
 * `requestOptions` as `makeRequest`'s 2nd argument only when the transport
 * actually has one (e.g. the public flow's `skipLoginRedirect`) — the admin
 * transport never sets one, so admin calls stay single-arg.
 */
export function makeTransportRequest<T>(
	transport: BulkSignTransport,
	params: BulkSignRequestParams
): Promise<T> {
	return transport.requestOptions
		? makeRequest<T>(params, transport.requestOptions)
		: makeRequest<T>(params);
}
